import { GameLayer } from './GameLayer';
import { Portal, TransferableEntity } from '@antification/protocol';
import { Dimensions } from './GameCanvas';
import {
  LayerInteraction,
  InteractionType,
  EntityTransferInteraction,
  StateQueryInteraction,
  StateQueryResponse,
} from './Interactions';

/**
 * Manages registration, updates, rendering, and interactions between game layers.
 * Replaces hardcoded dual-game setup to support N layers.
 */
export class LayerManager {
  /** Registered layers, keyed by layer ID */
  private layers: Map<string, GameLayer> = new Map();

  /** Layer order for update/render (first = bottom, last = top) */
  private layerOrder: string[] = [];

  /** Portal registry for quick lookup */
  private portalRegistry: Map<string, { portal: Portal; layerId: string }> = new Map();

  /** Pending interactions to process */
  private pendingInteractions: LayerInteraction[] = [];

  /** State query handlers registered by layers */
  private queryHandlers: Map<string, (query: StateQueryInteraction) => StateQueryResponse> = new Map();

  /**
   * Register a new layer.
   * @param layer The layer to register
   * @param position Optional position in layer order (0 = bottom). If not specified, added to top.
   */
  registerLayer(layer: GameLayer, position?: number): void {
    if (this.layers.has(layer.id)) {
      console.warn(`Layer ${layer.id} is already registered. Replacing.`);
      this.removeLayer(layer.id);
    }

    this.layers.set(layer.id, layer);

    // Add to order
    if (position !== undefined && position >= 0 && position <= this.layerOrder.length) {
      this.layerOrder.splice(position, 0, layer.id);
    } else {
      this.layerOrder.push(layer.id);
    }

    // Register layer's portals
    this.refreshPortals(layer.id);
  }

  /**
   * Remove a layer by ID.
   */
  removeLayer(id: string): void {
    if (!this.layers.has(id)) return;

    // Remove from registry
    this.layers.delete(id);

    // Remove from order
    const orderIndex = this.layerOrder.indexOf(id);
    if (orderIndex !== -1) {
      this.layerOrder.splice(orderIndex, 1);
    }

    // Remove portals
    for (const [portalId, data] of this.portalRegistry) {
      if (data.layerId === id) {
        this.portalRegistry.delete(portalId);
      }
    }

    // Remove query handler
    this.queryHandlers.delete(id);
  }

  /**
   * Get a layer by ID.
   */
  getLayer(id: string): GameLayer | undefined {
    return this.layers.get(id);
  }

  /**
   * Get all registered layers in order.
   */
  getLayers(): GameLayer[] {
    return this.layerOrder.map(id => this.layers.get(id)!).filter(Boolean);
  }

  /**
   * Get layer IDs in order.
   */
  getLayerIds(): string[] {
    return [...this.layerOrder];
  }

  /**
   * Get the number of registered layers.
   */
  getLayerCount(): number {
    return this.layers.size;
  }

  /**
   * Refresh portal registry for a specific layer.
   */
  refreshPortals(layerId: string): void {
    const layer = this.layers.get(layerId);
    if (!layer) return;

    // Remove old portals for this layer
    for (const [portalId, data] of this.portalRegistry) {
      if (data.layerId === layerId) {
        this.portalRegistry.delete(portalId);
      }
    }

    // Add current portals
    for (const portal of layer.getPortals()) {
      this.portalRegistry.set(portal.id, { portal, layerId });
    }
  }

  /**
   * Register a query handler for a layer.
   */
  registerQueryHandler(
    layerId: string,
    handler: (query: StateQueryInteraction) => StateQueryResponse
  ): void {
    this.queryHandlers.set(layerId, handler);
  }

  /**
   * Initialize all registered layers.
   */
  async initAll(): Promise<void> {
    const initPromises = this.getLayers().map(layer => layer.init());
    await Promise.all(initPromises);

    // Refresh all portals after init
    for (const id of this.layerOrder) {
      this.refreshPortals(id);
    }
  }

  /**
   * Update all layers.
   */
  update(dt: number): void {
    // Process pending interactions first
    this.processInteractions();

    // Update each layer
    for (const layer of this.getLayers()) {
      layer.update(dt);
    }
  }

  /**
   * Render layers to their respective contexts.
   * @param contexts Map of layer ID to canvas context
   */
  render(contexts: Map<string, CanvasRenderingContext2D>): void {
    for (const layer of this.getLayers()) {
      const ctx = contexts.get(layer.id);
      if (ctx) {
        layer.render(ctx);
      }
    }
  }

  /**
   * Resize all layers.
   * @param dimensions Map of layer ID to dimensions
   */
  resize(dimensions: Map<string, Dimensions>): void {
    for (const layer of this.getLayers()) {
      const dim = dimensions.get(layer.id);
      if (dim) {
        layer.resize(dim);
      }
    }
  }

  /**
   * Resize all layers to the same dimensions.
   */
  resizeAll(dimensions: Dimensions): void {
    for (const layer of this.getLayers()) {
      layer.resize(dimensions);
    }
  }

  /**
   * Transfer an entity between layers through portals.
   */
  transferEntity(
    entity: TransferableEntity,
    toLayerId: string,
    toPortalId: string
  ): boolean {
    const targetLayer = this.layers.get(toLayerId);
    if (!targetLayer) {
      console.warn(`Transfer failed: layer ${toLayerId} not found`);
      return false;
    }

    const targetPortalData = this.portalRegistry.get(toPortalId);
    if (!targetPortalData || targetPortalData.layerId !== toLayerId) {
      console.warn(`Transfer failed: portal ${toPortalId} not found in layer ${toLayerId}`);
      return false;
    }

    // Check if portal accepts this entity type
    const portal = targetPortalData.portal;
    if (!portal.entityTypes.includes(entity.type)) {
      console.warn(`Transfer failed: portal ${toPortalId} does not accept ${entity.type}`);
      return false;
    }

    // Check portal direction
    if (portal.type === 'exit') {
      console.warn(`Transfer failed: portal ${toPortalId} is exit-only`);
      return false;
    }

    // Deliver the entity — and report what the target layer did with it. Every
    // refusal above this line is a fault and says so; this one is the target
    // layer's own answer, and a layer is allowed to say no (ADR-028: an entrance
    // under a federal slab is closed).
    return targetLayer.receiveEntity(entity, toPortalId);
  }

  /**
   * Queue an interaction for processing.
   */
  queueInteraction(interaction: LayerInteraction): void {
    this.pendingInteractions.push(interaction);
  }

  /**
   * Execute a state query synchronously.
   */
  executeQuery(query: StateQueryInteraction): StateQueryResponse | null {
    const handler = this.queryHandlers.get(query.targetLayer as string);
    if (!handler) {
      console.warn(`No query handler for layer ${query.targetLayer}`);
      return null;
    }

    return handler(query);
  }

  /**
   * Process pending interactions.
   */
  private processInteractions(): void {
    const interactions = [...this.pendingInteractions];
    this.pendingInteractions = [];

    for (const interaction of interactions) {
      this.processInteraction(interaction);
    }
  }

  /**
   * Process a single interaction.
   */
  private processInteraction(interaction: LayerInteraction): void {
    switch (interaction.type) {
      case InteractionType.ENTITY_TRANSFER: {
        const transfer = interaction as EntityTransferInteraction;
        const entity: TransferableEntity = {
          type: transfer.payload.entityType,
          data: transfer.payload.entityData,
          sourcePortal: transfer.payload.sourcePortalId,
          sourceLayer: transfer.sourceLayer,
          timestamp: transfer.timestamp,
        };
        this.transferEntity(
          entity,
          transfer.targetLayer as string,
          transfer.payload.targetPortalId
        );
        break;
      }

      case InteractionType.STATE_QUERY: {
        // Queries should be executed synchronously via executeQuery
        // This is just for logging/debugging
        console.log('State query interaction processed:', interaction);
        break;
      }

      case InteractionType.EVENT:
      case InteractionType.EFFECT: {
        // These are handled via EventBus, not LayerManager
        // Just log for debugging
        console.log(`${interaction.type} interaction:`, interaction);
        break;
      }
    }
  }

  /**
   * Delegate mouse move event to a specific layer.
   */
  delegateMouseMove(layerId: string, e: MouseEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleMouseMove) {
      layer.handleMouseMove(e);
    }
  }

  /**
   * Delegate click event to a specific layer.
   */
  delegateClick(layerId: string, e: MouseEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleClick) {
      layer.handleClick(e);
    }
  }

  /**
   * Delegate mouse down event to a specific layer.
   */
  delegateMouseDown(layerId: string, e: MouseEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleMouseDown) {
      layer.handleMouseDown(e);
    }
  }

  /**
   * Delegate mouse up event to a specific layer.
   */
  delegateMouseUp(layerId: string, e: MouseEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleMouseUp) {
      layer.handleMouseUp(e);
    }
  }

  /**
   * Delegate keyboard events to a specific layer.
   */
  delegateKeyDown(layerId: string, e: KeyboardEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleKeyDown) {
      layer.handleKeyDown(e);
    }
  }

  delegateKeyUp(layerId: string, e: KeyboardEvent): void {
    const layer = this.layers.get(layerId);
    if (layer?.handleKeyUp) {
      layer.handleKeyUp(e);
    }
  }

  /**
   * Get a portal by ID.
   */
  getPortal(portalId: string): Portal | undefined {
    return this.portalRegistry.get(portalId)?.portal;
  }

  /**
   * Get all portals for a layer.
   */
  getLayerPortals(layerId: string): Portal[] {
    const result: Portal[] = [];
    for (const [, data] of this.portalRegistry) {
      if (data.layerId === layerId) {
        result.push(data.portal);
      }
    }
    return result;
  }

  /**
   * Get portals that connect two specific layers.
   */
  getConnectingPortals(layerId1: string, layerId2: string): Portal[] {
    const result: Portal[] = [];
    for (const [, data] of this.portalRegistry) {
      if (
        (data.layerId === layerId1 && data.portal.targetLayerId === layerId2) ||
        (data.layerId === layerId2 && data.portal.targetLayerId === layerId1)
      ) {
        result.push(data.portal);
      }
    }
    return result;
  }
}
